import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowClockwise, Table as TableIcon } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";

const monthLabel = (key) => {
    const [y, m] = key.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
};

/**
 * v1.21.0 — Party × Month reconcile grid.
 *
 * Green = fully reconciled, amber = partial, red = 0 reconciled but has txns,
 * grey = no activity, orange dot = has suspense entries.
 */
export default function ReconcileGrid() {
    const navigate = useNavigate();
    const [months, setMonths] = useState(6);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/reconcile-grid", { params: { months } });
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [months]);

    const gridMonths = data?.months || [];
    const rows = data?.rows || [];
    const totals = useMemo(() => {
        const t = { total: 0, reconciled: 0, suspense: 0 };
        for (const r of rows) {
            t.total += r.totals.total;
            t.reconciled += r.totals.reconciled;
            t.suspense += r.totals.suspense;
        }
        return t;
    }, [rows]);

    const openMonth = (partyId, mKey) => {
        const [y, m] = mKey.split("-");
        const last = new Date(Number(y), Number(m), 0).getDate();
        const df = `${mKey}-01`;
        const dt = `${mKey}-${String(last).padStart(2, "0")}`;
        navigate(`/admin/reports?partyId=${partyId}&date_from=${df}&date_to=${dt}&reconcile=1`);
    };

    const cellCls = (c) => {
        if (c.total === 0) return "bg-slate-50 text-slate-400";
        if (c.reconciled === c.total) return "bg-emerald-100 text-emerald-900 hover:bg-emerald-200 border-emerald-300";
        if (c.reconciled === 0) return "bg-rose-100 text-rose-900 hover:bg-rose-200 border-rose-300";
        return "bg-amber-100 text-amber-900 hover:bg-amber-200 border-amber-300";
    };

    return (
        <div className="space-y-6" data-testid="reconcile-grid-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md">
                            <TableIcon size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Reconcile Grid</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Every party × month at a glance. Click a cell to open that month's ledger in Reconcile mode.</p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Window</div>
                        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                            <SelectTrigger data-testid="reconcile-grid-months" className="mt-1 h-9 w-40 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {[3, 6, 9, 12, 18, 24].map((m) => <SelectItem key={m} value={String(m)}>Last {m} months</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <button onClick={load} disabled={loading} data-testid="reconcile-grid-refresh"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-100 disabled:opacity-50">
                        <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Active parties</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900" data-testid="grid-party-count">{rows.length}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Txns in window</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900" data-testid="grid-total">{totals.total}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800">Reconciled</div>
                    <div className="mt-2 font-display text-2xl font-bold text-emerald-900" data-testid="grid-reconciled">{totals.reconciled}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800">In suspense</div>
                    <div className="mt-2 font-display text-2xl font-bold text-amber-900" data-testid="grid-suspense">{totals.suspense}</div>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded border border-emerald-300 bg-emerald-100" /> All reconciled</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded border border-amber-300 bg-amber-100" /> Partial</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded border border-rose-300 bg-rose-100" /> Zero reconciled</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-slate-50 border border-slate-200" /> No activity</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-orange-500" /> Suspense present</span>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {rows.length === 0 && !loading ? (
                    <div className="p-6"><EmptyState icon={TableIcon} title="No activity" description="No parties have payments or payouts in this window." /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs" data-testid="reconcile-grid-table">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500">Party</th>
                                    {gridMonths.map((m) => (
                                        <th key={m} className="px-2 py-2 text-center text-[10px] uppercase text-slate-500 min-w-[80px]">{monthLabel(m)}</th>
                                    ))}
                                    <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-slate-500">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => (
                                    <tr key={r.party_id} data-testid={`grid-row-${r.party_id}`} className="hover:bg-slate-50">
                                        <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-slate-900 whitespace-nowrap">
                                            <button type="button" onClick={() => navigate(`/admin/reports?partyId=${r.party_id}&reconcile=1`)} className="hover:text-blue-700 hover:underline text-left">
                                                {r.party_name}
                                            </button>
                                        </td>
                                        {gridMonths.map((m) => {
                                            const c = r.cells[m] || { total: 0, reconciled: 0, suspense: 0 };
                                            return (
                                                <td key={m} className="p-1 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => c.total > 0 && openMonth(r.party_id, m)}
                                                        data-testid={`grid-cell-${r.party_id}-${m}`}
                                                        title={c.total > 0 ? `${c.reconciled}/${c.total} reconciled · ${c.suspense} in suspense · click to open` : "No activity"}
                                                        disabled={c.total === 0}
                                                        className={`relative w-full rounded border px-1 py-1.5 font-mono text-xs transition-colors ${cellCls(c)} ${c.total === 0 ? "cursor-default" : "cursor-pointer"}`}
                                                    >
                                                        {c.total === 0 ? "·" : `${c.reconciled}/${c.total}`}
                                                        {c.suspense > 0 && (
                                                            <span className="absolute top-0.5 right-0.5 inline-block h-2 w-2 rounded-full bg-orange-500" title={`${c.suspense} in suspense`} />
                                                        )}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                        <td className="px-3 py-2 text-center font-mono">
                                            <span className={r.totals.reconciled === r.totals.total ? "text-emerald-700 font-semibold" : (r.totals.reconciled === 0 ? "text-rose-700" : "text-amber-700 font-semibold")}>
                                                {r.totals.reconciled}/{r.totals.total}
                                            </span>
                                            {r.totals.suspense > 0 && (
                                                <span className="ml-1 rounded bg-orange-100 px-1 py-0.5 text-[9px] font-semibold text-orange-800">{r.totals.suspense}sp</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
